import { useEffect } from "react";
import { useUserStore } from "@/entities/user/model/store";
import { isSubscriptionResolved, resolveAccountId, selectOwnSubscription, } from "./selectors";
import { useSubscriptionStore } from "./store";
/**
 * Подписка текущего пользователя. Запрос уходит ТОЛЬКО для авторизованного
 * аккаунта: гостю и неавторизованному бэк её не отдаёт, и слать запрос незачем.
 *
 * Читает пользователя прямо из `useUserStore`, без `useAuth`: хук нужен и блоку
 * цен на SEO-лендинге, который не должен зависеть от контекста авторизации.
 */
export function useMySubscription(options = {}) {
    const { enabled = true, refreshOnMount = false } = options;
    const accountId = useUserStore(resolveAccountId);
    const isAuthLoading = useUserStore((s) => s.isLoading);
    const subscription = useSubscriptionStore((s) => s.subscription);
    const ownerId = useSubscriptionStore((s) => s.userId);
    const resolved = useSubscriptionStore((s) => s.resolved);
    const isCancelling = useSubscriptionStore((s) => s.isCancelling);
    const load = useSubscriptionStore((s) => s.load);
    const cancel = useSubscriptionStore((s) => s.cancel);
    const reset = useSubscriptionStore((s) => s.reset);
    useEffect(() => {
        // Пока идёт первичная проверка авторизации, в сторе лежит пользователь из
        // прошлого сеанса (persist) — ждём, чтобы не спросить подписку за гостя.
        if (isAuthLoading)
            return;
        if (accountId === null) {
            reset();
            return;
        }
        if (!enabled)
            return;
        void load(accountId, { force: refreshOnMount });
        // refreshOnMount читаем один раз — это свойство места, а не состояние.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountId, enabled, isAuthLoading, load, reset]);
    // Подписка из стора принадлежит именно текущему аккаунту.
    const own = selectOwnSubscription(accountId, ownerId, subscription);
    return {
        subscription: own,
        hasSubscription: own !== null,
        /**
         * Уже известно, есть ли подписка (для аккаунта — пришёл ответ по нему; для
         * гостя — сразу). Пока `false`, витрина не предлагает подписочные пакеты.
         */
        isResolved: isSubscriptionResolved(accountId, ownerId, resolved),
        isCancelling,
        cancel,
        reload: () => accountId === null ? Promise.resolve() : load(accountId, { force: true }),
    };
}
